import { useEffect, useState } from "react";

import dayjs from "dayjs";
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUpCircle,
  Box,
  Building2,
  Check,
  Droplet,
  Factory,
  MapPin,
  Package,
  Search,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  getStockAt,
  useOilTypeStockByPlant,
  useProductLotesForTransfer,
  useRawMaterialStockByPlant,
} from "~/hooks/purchasing";
import { useOilTypes } from "~/hooks/purchasing/useOilTypes";
import { useRawMaterials } from "~/hooks/purchasing/useRawMaterials";
import { useToast } from "~/hooks/use-toast";
import { useProducts } from "~/hooks/useProducts";
import {
  useAdjustmentReasonCodes,
  useCreateAdjustment,
  useWarehouses,
} from "~/hooks/wms";
import { useProductPositionInventory } from "~/hooks/wms/useInventory";
import { ingestionService } from "~/services/wms/ingestionService";
import { useProfileStore } from "~/store/profileStore";
import { OIL_UNITS } from "~/types/purchasing";
import { formatCurrency } from "~/utils/helpers";

import type {
  AdjustmentReasonCode,
  AdjustmentType,
  ItemType,
  Plant,
  StockAdjustmentFormData,
} from "~/types/stockAdjustment";

type SearchableItem = {
  id: string;
  name: string;
  code: string | null;
  stock: number;
  itemType: ItemType;
  unit?: string;
};

type Step =
  | "type"
  | "product"
  | "bodega"
  | "position"
  | "details"
  | "analysis"
  | "confirm";

const PLANTS: { value: Plant; label: string }[] = [
  { value: "P1", label: "Planta 1" },
  { value: "P2", label: "Planta 2" },
];

const itemTypeLabel = (t?: ItemType): string =>
  t === "oil" ? "Aceite" : t === "raw_material" ? "Materia Prima" : "Producto";

// Stable key for a producto-terminado lote group (lot_number + production_batch_id).
const adjLoteKey = (lot: string | null, batch: string | null) =>
  `${lot ?? "∅"}|${batch ?? "∅"}`;

interface StockAdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const DISCREPANCY_CAUSES = [
  { value: "picking_error", label: "Error de picking" },
  { value: "unregistered_reception", label: "Recepción no registrada" },
  { value: "warehouse_damage", label: "Daño/Quiebre en almacén" },
  { value: "physical_count_error", label: "Error de conteo físico" },
  { value: "expiration", label: "Producto vencido" },
  { value: "other", label: "Otro" },
];

interface ExtendedFormData extends Partial<StockAdjustmentFormData> {
  itemType?: ItemType;
  raw_material_id?: string;
  unit?: string;
}

export function StockAdjustmentDialog({
  open,
  onOpenChange,
  onSuccess,
}: StockAdjustmentDialogProps) {
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const createAdjustment = useCreateAdjustment();

  // Form state
  const [step, setStep] = useState<Step>("type");
  const [formData, setFormData] = useState<ExtendedFormData>({});
  const [productSearch, setProductSearch] = useState("");
  const [positionSearch, setPositionSearch] = useState("");
  const [positionSearchResult, setPositionSearchResult] = useState<{
    id: string;
    full_code: string;
  } | null>(null);
  const [positionSearchError, setPositionSearchError] = useState<string | null>(
    null
  );
  const [selectedReasonCode, setSelectedReasonCode] =
    useState<AdjustmentReasonCode | null>(null);
  // Selected warehouse (bodega) for product adjustments
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  // Available boxes of the selected lote (product decrement, warehouse-level)
  const [loteAvailable, setLoteAvailable] = useState<number | null>(null);

  // Data queries
  const { data: reasonCodes = [] } = useAdjustmentReasonCodes(
    formData.adjustment_type
  );
  const { products = [], loading: productsLoading } = useProducts();
  const { data: rawMaterials = [], isLoading: rawMaterialsLoading } =
    useRawMaterials();
  const { data: oilTypes = [], isLoading: oilTypesLoading } = useOilTypes();
  const { data: warehouses = [] } = useWarehouses();
  // Per-plant stock (source of truth) — used to weight the moving-average cost
  // preview the same way the create_stock_adjustment RPC does.
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawMaterialStockByPlant } = useRawMaterialStockByPlant();

  // Lotes available for the selected product per bodega (warehouse-level flow).
  const { data: productLotes = [] } = useProductLotesForTransfer(
    formData.itemType === "product" ? (formData.product_id ?? null) : null
  );

  // Get inventory positions for selected product (for decrements)
  const { data: productInventory = [], isLoading: inventoryLoading } =
    useProductPositionInventory(
      formData.itemType === "product" ? formData.product_id : undefined
    );

  // For products, narrow the position list to the selected bodega (warehouse)
  const filteredProductInventory = warehouseId
    ? productInventory.filter(
        (inv) => inv.position?.rack?.hallway?.warehouse_id === warehouseId
      )
    : productInventory;

  // Combine products and raw materials into unified searchable list
  const allItems: SearchableItem[] = [
    ...products.map((p) => ({
      id: p.id,
      name: p.short_name || "",
      code: p.sku || null,
      stock: p.stock ?? 0,
      itemType: "product" as const,
    })),
    ...rawMaterials.map((r) => ({
      id: r.id,
      name: r.name,
      code: r.code || null,
      stock: Number(r.current_stock) || 0,
      itemType: "raw_material" as const,
      unit: r.unit,
    })),
    ...oilTypes.map((o) => ({
      id: o.id,
      name: o.name,
      code: o.code || null,
      stock: Number(o.current_stock_liters) || 0,
      itemType: "oil" as const,
      unit: "L",
    })),
  ];

  const isLoading = productsLoading || rawMaterialsLoading || oilTypesLoading;

  // Filter items based on search
  const filteredItems = productSearch
    ? allItems.filter(
        (item) =>
          item.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
          item.code?.toLowerCase().includes(productSearch.toLowerCase())
      )
    : [];

  // Get selected item (product or raw material)
  const selectedItem = allItems.find(
    (item) =>
      (formData.itemType === "product" && item.id === formData.product_id) ||
      (formData.itemType === "raw_material" &&
        item.id === formData.raw_material_id) ||
      (formData.itemType === "oil" && item.id === formData.oil_type_id)
  );

  const selectedWarehouse = warehouses.find((w) => w.id === warehouseId);

  // Lotes of the product present in the chosen bodega (product decrement).
  // warehouse_id != null keeps this to ingested bodega lotes — the RPC also returns
  // un-ingested plant lotes (warehouse_id null), which share the "NC" code.
  const lotesInBodega = selectedWarehouse
    ? productLotes.filter(
        (l) => l.warehouse_id != null && l.warehouse_code === selectedWarehouse.code
      )
    : [];

  const selectedOil =
    formData.itemType === "oil"
      ? oilTypes.find((o) => o.id === formData.oil_type_id)
      : undefined;

  // Unit shown for the entered quantity vs the unit the stock is stored in.
  const stockUnitLabel =
    formData.itemType === "oil" ? "L" : selectedItem?.unit || "unidades";
  const entryUnitLabel =
    formData.itemType === "oil"
      ? (OIL_UNITS.find((u) => u.value === (formData.unit || "liters"))
          ?.label ??
        formData.unit ??
        "L")
      : selectedItem?.unit || "unidades";

  // Entered quantity converted to the stock unit (liters for oils) for the preview.
  const previewQuantityInStockUnit = (() => {
    const q = formData.quantity || 0;
    if (formData.itemType === "oil") {
      const density = selectedOil?.density_kg_per_liter || 0.92;
      if (formData.unit === "tonnes") return (q * 1000) / density;
      if (formData.unit === "kg") return q / density;
      return q; // liters / L
    }
    return q;
  })();

  const selectedRawMaterial =
    formData.itemType === "raw_material"
      ? rawMaterials.find((r) => r.id === formData.raw_material_id)
      : undefined;

  // Current weighted-average cost of the selected ingredient in its stock unit
  // (per LITER for oils, per material unit for raw materials).
  const currentAvgCost =
    formData.itemType === "oil"
      ? (selectedOil?.average_cost_clp ?? 0)
      : formData.itemType === "raw_material"
        ? (selectedRawMaterial?.average_cost_clp ?? 0)
        : 0;

  // Only ingredient ingresos (increments) capture a cost that re-weights the WAC.
  const isIngredientIncrement =
    (formData.itemType === "oil" || formData.itemType === "raw_material") &&
    formData.adjustment_type === "increment";

  // Cost basis unit the user enters: oils are entered per kg (POs are kg), raw
  // materials per their own unit. NOTE: oil stock/average_cost_clp are per LITER,
  // so create_stock_adjustment converts the entered $/kg to $/L (× density) before
  // re-weighting the WAC — the preview below mirrors that conversion.
  const costBasisUnit =
    formData.itemType === "oil" ? "kg" : selectedItem?.unit || "unidad";

  // Selected plant's pre-adjustment stock — the weight for the moving average.
  const selectedPlantStock =
    formData.itemType === "oil"
      ? getStockAt(
          oilStockByPlant,
          formData.oil_type_id ?? "",
          formData.plant ?? "P1"
        )
      : formData.itemType === "raw_material"
        ? getStockAt(
            rawMaterialStockByPlant,
            formData.raw_material_id ?? "",
            formData.plant ?? "P1"
          )
        : 0;

  // Resulting weighted-average cost preview (same formula as the RPC): the entered
  // cost weighted by the quantity in the stock unit (liters for oils, the material's
  // unit for raw materials). Oils: the entered $/kg is converted to $/L (× density)
  // to match the per-liter WAC — mirrors create_stock_adjustment.
  const previewNewAvgCost = (() => {
    const cost = formData.unit_cost;
    if (!isIngredientIncrement || cost == null || Number.isNaN(cost)) return null;
    const qty = previewQuantityInStockUnit;
    if (!qty || qty <= 0) return null;
    const costInStockUnit =
      formData.itemType === "oil"
        ? cost * (selectedOil?.density_kg_per_liter || 0.92)
        : cost;
    if (selectedPlantStock <= 0) return costInStockUnit;
    return (
      (selectedPlantStock * currentAvgCost + qty * costInStockUnit) /
      (selectedPlantStock + qty)
    );
  })();

  // Keep for backward compatibility in confirmation screen
  const selectedProduct = products.find((p) => p.id === formData.product_id);

  // Producto terminado cost: an ingreso (increment) with a cost re-weights the
  // finished-good moving-average cost (products.unit_cost_clp), weighted by the
  // product's total stock — mirrors the ingredient path server-side.
  const isProductIncrement =
    formData.itemType === "product" &&
    formData.adjustment_type === "increment";

  const productAvgCost = selectedProduct?.unit_cost_clp ?? 0;

  const previewProductNewAvgCost = (() => {
    const cost = formData.unit_cost;
    if (!isProductIncrement || cost == null || Number.isNaN(cost)) return null;
    const qty = formData.quantity || 0;
    if (qty <= 0) return null;
    const stock = selectedProduct?.stock ?? 0;
    if (stock <= 0) return cost;
    return (stock * productAvgCost + qty * cost) / (stock + qty);
  })();

  // Confirm-step "Antes → Después" for both stock and the moving-average cost,
  // covering every item type (products and ingredients).
  const beforeStock = selectedItem?.stock ?? 0;
  const afterStock =
    Math.round(
      (beforeStock +
        (formData.adjustment_type === "increment"
          ? previewQuantityInStockUnit
          : -previewQuantityInStockUnit)) *
        100
    ) / 100;
  const beforeAvgCost =
    formData.itemType === "product" ? productAvgCost : currentAvgCost;
  const afterAvgCost =
    formData.itemType === "product"
      ? (previewProductNewAvgCost ?? productAvgCost)
      : (previewNewAvgCost ?? currentAvgCost);
  const showCostComparison = beforeAvgCost > 0 || afterAvgCost > 0;

  const resetForm = () => {
    setStep("type");
    setFormData({});
    setProductSearch("");
    setPositionSearch("");
    setPositionSearchResult(null);
    setPositionSearchError(null);
    setSelectedReasonCode(null);
    setWarehouseId(null);
    setLoteAvailable(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // Handle position search for increments
  const handlePositionSearch = async () => {
    if (!positionSearch.trim()) return;

    setPositionSearchError(null);
    try {
      const result = await ingestionService.lookupPositionByBarcode(
        positionSearch.trim()
      );
      if (result.position) {
        setPositionSearchResult({
          id: result.position.id,
          full_code: result.position.full_code,
        });
        setFormData((prev) => ({
          ...prev,
          position_id: result.position!.id,
        }));
        setPositionSearchError(null);
      } else {
        setPositionSearchResult(null);
        setPositionSearchError(result.error || "Posición no encontrada");
      }
    } catch {
      setPositionSearchResult(null);
      setPositionSearchError("Error al buscar posición");
    }
  };

  // Handle inventory selection for decrements
  const handleInventorySelect = (inventory: {
    id: string;
    position_id: string;
  }) => {
    setFormData((prev) => ({
      ...prev,
      inventory_id: inventory.id,
      position_id: inventory.position_id,
    }));
  };

  const handleSelectType = (type: AdjustmentType) => {
    setFormData({ adjustment_type: type });
    setStep("product");
  };

  const handleSelectItem = (item: SearchableItem) => {
    setWarehouseId(null);
    setLoteAvailable(null);
    if (item.itemType === "product") {
      const product = products.find((p) => p.id === item.id);
      setFormData((prev) => ({
        ...prev,
        product_id: item.id,
        raw_material_id: undefined,
        oil_type_id: undefined,
        plant: undefined,
        position_id: undefined,
        inventory_id: undefined,
        itemType: "product",
        unit: undefined,
        // Prefill with the current WAC so leaving it unchanged keeps the average
        // steady; only meaningful for ingresos (increments).
        unit_cost:
          prev.adjustment_type === "increment"
            ? (product?.unit_cost_clp ?? undefined)
            : undefined,
      }));
    } else if (item.itemType === "raw_material") {
      const material = rawMaterials.find((r) => r.id === item.id);
      setFormData((prev) => ({
        ...prev,
        raw_material_id: item.id,
        product_id: undefined,
        oil_type_id: undefined,
        position_id: undefined,
        inventory_id: undefined,
        itemType: "raw_material",
        unit: item.unit,
        // Prefill the cost with the current average so leaving it as-is keeps
        // the WAC unchanged; only meaningful for ingresos (increments).
        unit_cost:
          prev.adjustment_type === "increment"
            ? (material?.average_cost_clp ?? undefined)
            : undefined,
      }));
    } else {
      const oil = oilTypes.find((o) => o.id === item.id);
      setFormData((prev) => ({
        ...prev,
        oil_type_id: item.id,
        product_id: undefined,
        raw_material_id: undefined,
        position_id: undefined,
        inventory_id: undefined,
        itemType: "oil",
        unit: "liters",
        // Oil WAC is stored per liter and the default entry unit is liters, so
        // the prefilled cost is the per-liter average.
        unit_cost:
          prev.adjustment_type === "increment"
            ? (oil?.average_cost_clp ?? undefined)
            : undefined,
      }));
    }
    setProductSearch("");
    setStep("bodega");
  };

  const handleReasonCodeChange = (codeId: string) => {
    const code = reasonCodes.find((c) => c.id === codeId);
    setSelectedReasonCode(code || null);
    setFormData((prev) => ({ ...prev, reason_code_id: codeId }));
  };

  // Product decrement: pick which lote (bucket row) to reduce in the chosen bodega.
  const handleSelectAdjustLote = (key: string) => {
    const lote = lotesInBodega.find(
      (l) => adjLoteKey(l.lot_number, l.production_batch_id) === key
    );
    if (!lote) return;
    setFormData((prev) => ({
      ...prev,
      lot_number: lote.lot_number ?? undefined,
      production_batch_id: lote.production_batch_id,
    }));
    setLoteAvailable(lote.available_boxes);
  };

  // Auto-select when the product has a single lote in the chosen bodega.
  useEffect(() => {
    if (
      formData.itemType === "product" &&
      formData.adjustment_type === "decrement" &&
      lotesInBodega.length === 1 &&
      loteAvailable === null
    ) {
      handleSelectAdjustLote(
        adjLoteKey(
          lotesInBodega[0].lot_number,
          lotesInBodega[0].production_batch_id
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotesInBodega, loteAvailable, formData.itemType, formData.adjustment_type]);

  const handleSubmit = async () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar al usuario",
        variant: "destructive",
      });
      return;
    }

    try {
      // Build the adjustment data with item_type
      const adjustmentData: StockAdjustmentFormData = {
        adjustment_type: formData.adjustment_type!,
        reason_code_id: formData.reason_code_id!,
        item_type: formData.itemType ?? "product",
        quantity: formData.quantity!,
        notes: formData.notes,
        discrepancy_cause: formData.discrepancy_cause,
        lot_number: formData.lot_number,
        expiration_date: formData.expiration_date,
        destruction_certificate_number: formData.destruction_certificate_number,
        destruction_certificate_date: formData.destruction_certificate_date,
      };

      if (formData.itemType === "raw_material") {
        adjustmentData.raw_material_id = formData.raw_material_id;
        adjustmentData.unit = formData.unit || "units";
        adjustmentData.plant = formData.plant;
        adjustmentData.unit_cost =
          formData.adjustment_type === "increment"
            ? formData.unit_cost
            : undefined;
      } else if (formData.itemType === "oil") {
        adjustmentData.oil_type_id = formData.oil_type_id;
        adjustmentData.unit = formData.unit || "liters";
        adjustmentData.plant = formData.plant;
        adjustmentData.unit_cost =
          formData.adjustment_type === "increment"
            ? formData.unit_cost
            : undefined;
      } else {
        // Warehouse-level product adjustment (routes through the RPC in the service)
        adjustmentData.product_id = formData.product_id;
        adjustmentData.warehouse_code = selectedWarehouse?.code;
        adjustmentData.production_batch_id = formData.production_batch_id ?? null;
        // Re-weights products.unit_cost_clp on an ingreso (increment); ignored on
        // decrements. Undefined keeps the WAC unchanged.
        adjustmentData.unit_cost =
          formData.adjustment_type === "increment"
            ? formData.unit_cost
            : undefined;
      }

      await createAdjustment.mutateAsync({
        data: adjustmentData,
        performedBy: userId,
      });

      toast({
        title: "Ajuste creado",
        description: `Se ${formData.adjustment_type === "increment" ? "agregaron" : "retiraron"} ${formData.quantity} ${entryUnitLabel}`,
      });

      handleClose();
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "No se pudo crear el ajuste",
        variant: "destructive",
      });
    }
  };

  const canProceed = () => {
    switch (step) {
      case "type":
        return !!formData.adjustment_type;
      case "product":
        return !!(
          formData.product_id ||
          formData.raw_material_id ||
          formData.oil_type_id
        );
      case "bodega":
        // Insumos pick a plant (P1/P2); products pick a warehouse (bodega)
        return formData.itemType === "product"
          ? !!warehouseId
          : !!formData.plant;
      case "position":
        // Position step only for products
        if (formData.itemType !== "product") return true;
        if (formData.adjustment_type === "increment") {
          return !!formData.position_id; // Must select a position for increment
        } else {
          return !!formData.inventory_id; // Must select specific inventory for decrement
        }
      case "details":
        if (!formData.quantity || formData.quantity <= 0) return false;
        if (!formData.reason_code_id) return false;
        if (
          formData.adjustment_type === "decrement" &&
          formData.itemType === "product"
        ) {
          if (loteAvailable == null) return false; // must pick a lote to reduce
          if (formData.quantity > loteAvailable) return false;
        }
        if (
          formData.adjustment_type === "increment" &&
          formData.itemType === "product"
        ) {
          if (selectedReasonCode?.requires_lot_number && !formData.lot_number)
            return false;
          if (
            selectedReasonCode?.requires_expiration_date &&
            !formData.expiration_date
          )
            return false;
        }
        return true;
      case "analysis":
        return true; // Analysis is optional
      case "confirm":
        return true;
      default:
        return false;
    }
  };

  // Positions are hidden for the August go-live: products adjust at bodega level
  // (B1/B2/NC) via the create_product_stock_adjustment RPC, insumos at plant level.
  const getSteps = (): Step[] => [
    "type",
    "product",
    "bodega",
    "details",
    "analysis",
    "confirm",
  ];

  const goBack = () => {
    const steps = getSteps();
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };

  const goNext = () => {
    const steps = getSteps();
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuevo Ajuste de Stock</DialogTitle>
          <DialogDescription>
            {step === "type" && "Selecciona el tipo de ajuste"}
            {step === "product" && "Selecciona el item a ajustar"}
            {step === "bodega" &&
              (formData.itemType === "product"
                ? "Selecciona la bodega"
                : "Selecciona la planta / bodega")}
            {step === "position" &&
              (formData.adjustment_type === "increment"
                ? "Selecciona la posición de destino"
                : "Selecciona de dónde retirar stock")}
            {step === "details" && "Ingresa los detalles del ajuste"}
            {step === "analysis" && "Análisis de discrepancia (opcional)"}
            {step === "confirm" && "Confirma el ajuste"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Step 1: Select Type */}
          {step === "type" && (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleSelectType("increment")}
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-green-200 bg-green-50 p-6 transition-all hover:border-green-400 hover:bg-green-100"
              >
                <ArrowUpCircle className="h-12 w-12 text-green-600" />
                <div className="text-center">
                  <div className="font-semibold text-green-700">Incremento</div>
                  <div className="text-sm text-green-600">
                    Agregar stock (+)
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleSelectType("decrement")}
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-red-200 bg-red-50 p-6 transition-all hover:border-red-400 hover:bg-red-100"
              >
                <ArrowDownCircle className="h-12 w-12 text-red-600" />
                <div className="text-center">
                  <div className="font-semibold text-red-700">Decremento</div>
                  <div className="text-sm text-red-600">Quitar stock (-)</div>
                </div>
              </button>
            </div>
          )}

          {/* Step 2: Select Product or Raw Material */}
          {step === "product" && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, SKU o código..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {productSearch && (
                <div className="max-h-[300px] overflow-y-auto rounded-lg border">
                  {isLoading ? (
                    <div className="p-4 text-center text-muted-foreground">
                      Cargando...
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No se encontraron items
                    </div>
                  ) : (
                    filteredItems.slice(0, 10).map((item) => (
                      <button
                        key={`${item.itemType}-${item.id}`}
                        onClick={() => handleSelectItem(item)}
                        className="flex w-full items-center gap-3 border-b p-3 text-left transition-colors last:border-b-0 hover:bg-muted"
                      >
                        {item.itemType === "product" ? (
                          <Package className="h-5 w-5 text-muted-foreground" />
                        ) : item.itemType === "oil" ? (
                          <Droplet className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <Box className="h-5 w-5 text-muted-foreground" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{item.name}</span>
                            <Badge
                              variant={
                                item.itemType === "product"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs"
                            >
                              {itemTypeLabel(item.itemType)}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Código: {item.code || "-"} | Stock: {item.stock}{" "}
                            {item.unit || "unidades"}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {selectedItem && (
                <div className="rounded-lg border bg-muted/50 p-4">
                  <div className="flex items-center gap-3">
                    {selectedItem.itemType === "product" ? (
                      <Package className="h-8 w-8 text-primary" />
                    ) : selectedItem.itemType === "oil" ? (
                      <Droplet className="h-8 w-8 text-primary" />
                    ) : (
                      <Box className="h-8 w-8 text-primary" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          {selectedItem.name}
                        </span>
                        <Badge
                          variant={
                            selectedItem.itemType === "product"
                              ? "default"
                              : "secondary"
                          }
                          className="text-xs"
                        >
                          {itemTypeLabel(selectedItem.itemType)}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Stock actual: {selectedItem.stock}{" "}
                        {selectedItem.unit || "unidades"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Bodega (warehouse for products) / Planta (P1-P2 for insumos) */}
          {step === "bodega" && (
            <div className="space-y-4">
              {formData.itemType === "product" ? (
                <>
                  <Label>Selecciona la bodega</Label>
                  <div className="space-y-2">
                    {warehouses
                      .filter((w) => w.is_active)
                      .map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => {
                            setWarehouseId(w.id);
                            setLoteAvailable(null);
                            setFormData((prev) => ({
                              ...prev,
                              position_id: undefined,
                              inventory_id: undefined,
                              lot_number: undefined,
                              production_batch_id: undefined,
                            }));
                          }}
                          className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-all ${
                            warehouseId === w.id
                              ? "border-primary bg-primary/10"
                              : "hover:border-muted-foreground/50 hover:bg-muted/50"
                          }`}
                        >
                          <Building2
                            className={`h-6 w-6 ${
                              warehouseId === w.id
                                ? "text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                          <div>
                            <div className="font-medium">{w.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {w.code}
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                </>
              ) : (
                <>
                  <Label>Selecciona la planta / bodega</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {PLANTS.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, plant: p.value }))
                        }
                        className={`flex flex-col items-center gap-2 rounded-lg border-2 p-6 transition-all ${
                          formData.plant === p.value
                            ? "border-primary bg-primary/10"
                            : "border-muted hover:border-muted-foreground/50 hover:bg-muted/50"
                        }`}
                      >
                        <Factory
                          className={`h-10 w-10 ${
                            formData.plant === p.value
                              ? "text-primary"
                              : "text-muted-foreground"
                          }`}
                        />
                        <div className="text-center">
                          <div className="font-semibold">{p.label}</div>
                          <div className="text-sm text-muted-foreground">
                            {p.value}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedItem && (
                    <p className="text-sm text-muted-foreground">
                      Ajustando{" "}
                      <span className="font-medium text-foreground">
                        {selectedItem.name}
                      </span>{" "}
                      en la planta seleccionada.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Position Selection (products only) */}
          {step === "position" && formData.itemType === "product" && (
            <div className="space-y-4">
              {formData.adjustment_type === "increment" ? (
                // INCREMENT: Search for position to add stock
                <>
                  <div className="space-y-2">
                    <Label>Escanear o buscar posición</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Código de posición (ej: BOD-001-HA-R01-P03)"
                        value={positionSearch}
                        onChange={(e) => setPositionSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handlePositionSearch();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePositionSearch}
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                    {positionSearchError && (
                      <p className="text-sm text-red-500">
                        {positionSearchError}
                      </p>
                    )}
                  </div>

                  {positionSearchResult && (
                    <div className="rounded-lg border bg-green-50 p-4">
                      <div className="flex items-center gap-3">
                        <MapPin className="h-8 w-8 text-green-600" />
                        <div>
                          <div className="font-semibold text-green-700">
                            {positionSearchResult.full_code}
                          </div>
                          <div className="text-sm text-green-600">
                            Posición seleccionada
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // DECREMENT: Show available positions with inventory
                <>
                  <div className="space-y-2">
                    <Label>
                      Posiciones con stock de {selectedItem?.name}
                      {selectedWarehouse ? ` en ${selectedWarehouse.name}` : ""}
                    </Label>
                    {inventoryLoading ? (
                      <div className="p-4 text-center text-muted-foreground">
                        Cargando inventario...
                      </div>
                    ) : filteredProductInventory.length === 0 ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                          <div className="text-sm text-amber-800">
                            No hay stock de este producto en posiciones de{" "}
                            {selectedWarehouse?.name ?? "esta bodega"}.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="max-h-[300px] space-y-2 overflow-y-auto">
                        {filteredProductInventory.map((inv) => (
                          <button
                            key={inv.id}
                            type="button"
                            onClick={() => handleInventorySelect(inv)}
                            className={`w-full rounded-lg border p-3 text-left transition-all ${
                              formData.inventory_id === inv.id
                                ? "border-primary bg-primary/10"
                                : "hover:border-muted-foreground/50 hover:bg-muted/50"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <MapPin
                                  className={`h-5 w-5 ${
                                    formData.inventory_id === inv.id
                                      ? "text-primary"
                                      : "text-muted-foreground"
                                  }`}
                                />
                                <div>
                                  <div className="font-medium">
                                    {inv.position?.full_code || "Sin posición"}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {inv.lot_number &&
                                      `Lote: ${inv.lot_number}`}
                                    {inv.lot_number &&
                                      inv.expiration_date &&
                                      " | "}
                                    {inv.expiration_date &&
                                      `Vence: ${dayjs(inv.expiration_date).format("DD/MM/YYYY")}`}
                                  </div>
                                </div>
                              </div>
                              <Badge variant="secondary">
                                {inv.box_count} cajas
                              </Badge>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Details */}
          {step === "details" && (
            <div className="space-y-4">
              {formData.itemType === "product" &&
                formData.adjustment_type === "decrement" && (
                  <div className="space-y-2">
                    <Label>Lote a descontar</Label>
                    <Select
                      value={
                        formData.lot_number || formData.production_batch_id
                          ? adjLoteKey(
                              formData.lot_number ?? null,
                              formData.production_batch_id ?? null
                            )
                          : ""
                      }
                      onValueChange={handleSelectAdjustLote}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue
                          placeholder={
                            lotesInBodega.length === 0
                              ? `Sin stock en ${selectedWarehouse?.code ?? "la bodega"}`
                              : "Selecciona un lote"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {lotesInBodega.map((l) => (
                          <SelectItem
                            key={adjLoteKey(l.lot_number, l.production_batch_id)}
                            value={adjLoteKey(
                              l.lot_number,
                              l.production_batch_id
                            )}
                          >
                            {l.lote_label} — {l.available_boxes} cajas
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {loteAvailable != null && (
                      <p className="text-xs text-muted-foreground">
                        Disponible: {loteAvailable} cajas
                      </p>
                    )}
                  </div>
                )}

              <div className="space-y-2">
                <Label htmlFor="quantity">Cantidad</Label>
                <div className="flex gap-2">
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    step={formData.itemType === "product" ? "1" : "0.01"}
                    value={formData.quantity || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        quantity:
                          (formData.itemType === "product"
                            ? parseInt(e.target.value)
                            : parseFloat(e.target.value)) || 0,
                      }))
                    }
                    placeholder="Cantidad a ajustar"
                    className="flex-1"
                  />
                  {formData.itemType === "oil" && (
                    <Select
                      value={formData.unit || "liters"}
                      onValueChange={(v) =>
                        setFormData((prev) => ({ ...prev, unit: v }))
                      }
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OIL_UNITS.map((u) => (
                          <SelectItem key={u.value} value={u.value}>
                            {u.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {formData.itemType === "raw_material" &&
                    selectedItem?.unit && (
                      <div className="flex items-center px-3 text-sm text-muted-foreground">
                        {selectedItem.unit}
                      </div>
                    )}
                </div>
              </div>

              {isIngredientIncrement && (
                <div className="space-y-2">
                  <Label htmlFor="unit_cost">Costo unitario ($/{costBasisUnit})</Label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="unit_cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unit_cost ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          unit_cost:
                            e.target.value === ""
                              ? undefined
                              : parseFloat(e.target.value),
                        }))
                      }
                      placeholder="Costo del ingreso"
                      className="h-9 pl-6"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {previewNewAvgCost != null ? (
                      <>
                        Costo promedio: ${formatCurrency(currentAvgCost)} → nuevo{" "}
                        <span className="font-medium text-foreground">
                          ${formatCurrency(previewNewAvgCost)}
                        </span>{" "}
                        /{costBasisUnit}
                      </>
                    ) : (
                      <>
                        Costo promedio actual: ${formatCurrency(currentAvgCost)} /
                        {costBasisUnit}. Déjalo igual para no alterar el promedio.
                      </>
                    )}
                  </p>
                </div>
              )}

              {isProductIncrement && (
                <div className="space-y-2">
                  <Label htmlFor="unit_cost">Costo unitario ($/unidad)</Label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="unit_cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unit_cost ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          unit_cost:
                            e.target.value === ""
                              ? undefined
                              : parseFloat(e.target.value),
                        }))
                      }
                      placeholder="Costo del ingreso"
                      className="h-9 pl-6"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {previewProductNewAvgCost != null ? (
                      <>
                        Costo promedio: ${formatCurrency(productAvgCost)} → nuevo{" "}
                        <span className="font-medium text-foreground">
                          ${formatCurrency(previewProductNewAvgCost)}
                        </span>{" "}
                        /unidad
                      </>
                    ) : (
                      <>
                        Costo promedio actual: ${formatCurrency(productAvgCost)}{" "}
                        /unidad. Déjalo igual para no alterar el promedio.
                      </>
                    )}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="reason">Código de Razón</Label>
                <Select
                  value={formData.reason_code_id || ""}
                  onValueChange={handleReasonCodeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una razón" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasonCodes.map((code) => (
                      <SelectItem key={code.id} value={code.id}>
                        {code.code}: {code.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedReasonCode?.description && (
                  <p className="text-sm text-muted-foreground">
                    {selectedReasonCode.description}
                  </p>
                )}
              </div>

              {formData.adjustment_type === "increment" &&
                formData.itemType === "product" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="lot_number">
                        Número de Lote{" "}
                        {selectedReasonCode?.requires_lot_number && (
                          <span className="text-red-500">*</span>
                        )}
                      </Label>
                      <Input
                        id="lot_number"
                        value={formData.lot_number || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            lot_number: e.target.value,
                          }))
                        }
                        placeholder="Ej: LOT-2026-001"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expiration_date">
                        Fecha de Vencimiento{" "}
                        {selectedReasonCode?.requires_expiration_date && (
                          <span className="text-red-500">*</span>
                        )}
                      </Label>
                      <Input
                        id="expiration_date"
                        type="date"
                        value={formData.expiration_date || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            expiration_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </>
                )}

              {selectedReasonCode?.requires_destruction_certificate && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                    <div className="text-sm text-amber-800">
                      <strong>Certificado de destrucción requerido.</strong>
                      <p className="mt-1">
                        Este tipo de ajuste requiere documentación para el
                        certificado de destrucción.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Analysis */}
          {step === "analysis" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="discrepancy_cause">Causa probable</Label>
                <Select
                  value={formData.discrepancy_cause || ""}
                  onValueChange={(v) =>
                    setFormData((prev) => ({ ...prev, discrepancy_cause: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una causa" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCREPANCY_CAUSES.map((cause) => (
                      <SelectItem key={cause.value} value={cause.value}>
                        {cause.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notas adicionales</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Describe la situación que originó este ajuste..."
                  rows={4}
                />
              </div>

              {selectedReasonCode?.requires_destruction_certificate && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="destruction_certificate_number">
                      Número de Certificado de Destrucción
                    </Label>
                    <Input
                      id="destruction_certificate_number"
                      value={formData.destruction_certificate_number || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          destruction_certificate_number: e.target.value,
                        }))
                      }
                      placeholder="Ej: CD-2026-001"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="destruction_certificate_date">
                      Fecha del Certificado
                    </Label>
                    <Input
                      id="destruction_certificate_date"
                      type="date"
                      value={formData.destruction_certificate_date || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          destruction_certificate_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 5: Confirm */}
          {step === "confirm" && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/50 p-4">
                <h4 className="mb-3 font-semibold">Resumen del Ajuste</h4>

                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Tipo:</dt>
                    <dd>
                      <Badge
                        variant={
                          formData.adjustment_type === "increment"
                            ? "default"
                            : "destructive"
                        }
                      >
                        {formData.adjustment_type === "increment"
                          ? "Incremento (+)"
                          : "Decremento (-)"}
                      </Badge>
                    </dd>
                  </div>

                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Item:</dt>
                    <dd className="flex items-center gap-2">
                      <span className="font-medium">{selectedItem?.name}</span>
                      <Badge
                        variant={
                          formData.itemType === "product"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {itemTypeLabel(formData.itemType)}
                      </Badge>
                    </dd>
                  </div>

                  {formData.itemType === "product" && selectedWarehouse && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Bodega:</dt>
                      <dd className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {selectedWarehouse.name}
                      </dd>
                    </div>
                  )}

                  {formData.itemType !== "product" && formData.plant && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Planta:</dt>
                      <dd className="flex items-center gap-1">
                        <Factory className="h-3 w-3" />
                        {PLANTS.find((p) => p.value === formData.plant)?.label}
                      </dd>
                    </div>
                  )}

                  {formData.itemType === "product" && formData.position_id && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Posición:</dt>
                      <dd className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {formData.adjustment_type === "increment"
                          ? positionSearchResult?.full_code
                          : productInventory.find(
                              (inv) => inv.id === formData.inventory_id
                            )?.position?.full_code}
                      </dd>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Cantidad:</dt>
                    <dd
                      className={`font-bold ${
                        formData.adjustment_type === "increment"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formData.adjustment_type === "increment" ? "+" : "-"}
                      {formData.quantity} {entryUnitLabel}
                    </dd>
                  </div>

                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Razón:</dt>
                    <dd>
                      {selectedReasonCode?.code}: {selectedReasonCode?.name}
                    </dd>
                  </div>

                  {(isIngredientIncrement || isProductIncrement) &&
                    formData.unit_cost != null && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Costo unitario:</dt>
                        <dd>
                          ${formatCurrency(formData.unit_cost)} /
                          {formData.itemType === "product"
                            ? "unidad"
                            : costBasisUnit}
                        </dd>
                      </div>
                    )}

                  {formData.lot_number && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Lote:</dt>
                      <dd>{formData.lot_number}</dd>
                    </div>
                  )}

                  {formData.expiration_date && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Vencimiento:</dt>
                      <dd>
                        {dayjs(formData.expiration_date).format("DD/MM/YYYY")}
                      </dd>
                    </div>
                  )}

                  {/* Antes → Después: stock and moving-average cost */}
                  <div className="my-2 space-y-1.5 border-t pt-3">
                    <div className="grid grid-cols-3 gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <span />
                      <span className="text-right">Antes</span>
                      <span className="text-right">Después</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <span className="text-muted-foreground">Stock</span>
                      <span className="text-right">
                        {beforeStock} {stockUnitLabel}
                      </span>
                      <span
                        className={`text-right font-semibold ${
                          afterStock > beforeStock
                            ? "text-green-700"
                            : afterStock < beforeStock
                              ? "text-red-700"
                              : ""
                        }`}
                      >
                        {afterStock} {stockUnitLabel}
                      </span>
                    </div>
                    {showCostComparison && (
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="text-muted-foreground">
                          Costo prom.
                        </span>
                        <span className="text-right">
                          ${formatCurrency(beforeAvgCost)}
                        </span>
                        <span
                          className={`text-right font-semibold ${
                            afterAvgCost > beforeAvgCost
                              ? "text-green-700"
                              : afterAvgCost < beforeAvgCost
                                ? "text-red-700"
                                : ""
                          }`}
                        >
                          ${formatCurrency(afterAvgCost)}
                        </span>
                      </div>
                    )}
                  </div>
                </dl>
              </div>

              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>Esta acción no se puede deshacer.</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          {step !== "type" && (
            <Button variant="outline" onClick={goBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Atrás
            </Button>
          )}

          <div className="flex-1" />

          {step === "confirm" ? (
            <Button
              onClick={handleSubmit}
              disabled={createAdjustment.isPending}
            >
              {createAdjustment.isPending ? (
                "Procesando..."
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Confirmar Ajuste
                </>
              )}
            </Button>
          ) : step !== "type" ? (
            <Button onClick={goNext} disabled={!canProceed()}>
              Continuar
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
